import { useEffect, useRef, useState } from "react";
import { ColyseusClientWrapper } from "@/lib/colyseus/ColyseusClient";
import { globalEventBus } from "@/lib/realtime/EventBus.js";
import { ColyseusContext } from "@/contexts/ColyseusContext.js";

// Helpers to map Colyseus state to plain JS objects used in React
function mapLobbyState(state) {
  const players = [];
  const tables = [];

  try {
    const playersContainer = state?.onlinePlayers || state?.players;
    if (playersContainer?.forEach) {
      playersContainer.forEach((p, key) => {
        players.push({
          id: p?.id || key,
          username: p?.username,
          characterId: p?.characterId,
          isReady: !!p?.isReady,
          isHost: !!p?.isHost,
          connectedAt: p?.connectedAt,
        });
      });
    }
  } catch (e) {
    console.error("[ColyseusProvider] mapLobbyState players error:", e);
  }

  try {
    const tablesContainer = state?.tables;
    if (tablesContainer?.forEach) {
      tablesContainer.forEach((t, key) => {
        tables.push({
          id: t?.id || key,
          name: t?.name,
          mode: t?.mode,
          maxPlayers: t?.maxPlayers,
          currentPlayers: t?.currentPlayers,
          status: t?.status,
          hostId: t?.hostId,
          isPrivate: !!t?.isPrivate,
        });
      });
    }
  } catch (e) {
    console.error("[ColyseusProvider] mapLobbyState tables error:", e);
  }

  return {
    onlinePlayers: players,
    tables,
    messages: [],
  };
}

function hasPlayersChanged(prev, next) {
  const prevPlayers = Array.isArray(prev?.onlinePlayers)
    ? prev.onlinePlayers
    : [];
  const newPlayers = Array.isArray(next?.onlinePlayers)
    ? next.onlinePlayers
    : [];
  if (prevPlayers.length !== newPlayers.length) return true;
  return !newPlayers.every((np) => {
    const pp = prevPlayers.find((p) => p.id === np.id);
    return (
      pp &&
      pp.username === np.username &&
      pp.isHost === np.isHost &&
      pp.isReady === np.isReady
    );
  });
}

export function ColyseusProvider({ children }) {
  const getPersistedUsername = () => {
    try {
      const raw = localStorage.getItem("gg_user");
      if (!raw) return null;
      const user = JSON.parse(raw);
      return user?.username || null;
    } catch {
      return null;
    }
  };

  const [username, setUsername] = useState(getPersistedUsername());
  const [connected, setConnected] = useState(false);
  const [connectionAttempted, setConnectionAttempted] = useState(false);
  const [lastError, setLastError] = useState(null);
  const [lobbyState, setLobbyState] = useState({
    tables: [],
    messages: [],
    onlinePlayers: [],
  });

  const clientRef = useRef(null);
  const lobbyRoomRef = useRef(null);

  // Bootstrap client and ensure cleanup on reload/navigation
  useEffect(() => {
    const url = import.meta.env.VITE_COLYSEUS_URL || "ws://localhost:2567";
    clientRef.current = new ColyseusClientWrapper({ url });
    const handleBeforeUnload = () => {
      try {
        clientRef.current?.leaveLobby();
      } catch (err) {
        console.error("[ColyseusProvider] Cleanup error:", err);
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      clientRef.current?.leaveLobby();
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // Connect to lobby whenever we have a username
  useEffect(() => {
    if (!clientRef.current || !username) {
      return;
    }

    let disposed = false;
    let currentRoom = null;

    (async () => {
      setConnectionAttempted(true);
      setLastError(null);

      try {
        const room = await clientRef.current.connectLobby({ username });

        if (disposed) {
          await room.leave(true);
          return;
        }

        currentRoom = room;
        lobbyRoomRef.current = room;

        room.onError((code, message) => {
          console.error("[ColyseusProvider] Room error:", code, message);
          setLastError(`${code}: ${message}`);
        });

        room.onLeave(() => {
          if (!disposed) {
            setConnected(false);
            setLobbyState({ tables: [], messages: [], onlinePlayers: [] });
          }
        });

        if (!disposed && room.state) {
          setLobbyState(mapLobbyState(room.state));
        }

        room.onStateChange((state) => {
          if (disposed) return;
          const mapped = mapLobbyState(state);
          setLobbyState((prevState) =>
            hasPlayersChanged(prevState, mapped) ? mapped : prevState
          );
        });

        // Manual initial players iteration for MapSchema
        const playersContainer =
          room.state?.onlinePlayers || room.state?.players;
        if (playersContainer) {
          const manualPlayers = [];
          try {
            if (playersContainer.forEach) {
              playersContainer.forEach((p, key) => {
                manualPlayers.push({
                  id: p.id || key,
                  username: p.username,
                  characterId: p.characterId,
                  isReady: p.isReady,
                  isHost: p.isHost,
                  connectedAt: p.connectedAt,
                });
              });
            }
            if (manualPlayers.length > 0) {
              setLobbyState((prev) => ({
                ...prev,
                onlinePlayers: manualPlayers,
              }));
            }
          } catch (e) {
            console.error("[ColyseusProvider] Error iterating players:", e);
          }
        }

        // Attach add/remove handlers if present
        try {
          const playersMapForHandlers =
            room.state?.onlinePlayers || room.state?.players;
          if (playersMapForHandlers?.onAdd) {
            playersMapForHandlers.onAdd((player, key) => {
              if (disposed) return;
              setLobbyState((prev) => {
                const existing = prev?.onlinePlayers || [];
                if (existing.some((p) => p.id === player.id || p.id === key)) {
                  return prev;
                }
                return {
                  ...prev,
                  onlinePlayers: [
                    ...existing,
                    {
                      id: player.id || key,
                      username: player.username,
                      characterId: player.characterId,
                      isReady: player.isReady,
                      isHost: player.isHost,
                      connectedAt: player.connectedAt,
                    },
                  ],
                };
              });
            });
          }

          if (playersMapForHandlers?.onRemove) {
            playersMapForHandlers.onRemove((player, key) => {
              if (disposed) return;
              setLobbyState((prev) => {
                const existing = prev?.onlinePlayers || [];
                return {
                  ...prev,
                  onlinePlayers: existing.filter(
                    (p) => p.id !== (player.id || key)
                  ),
                };
              });
            });
          }
        } catch (e) {
          console.error("[ColyseusProvider] Error attaching handlers:", e);
        }

        // Messages from server to sync lobby
        room.onMessage("tableCreatedSuccess", (payload) => {
          console.log(
            "[ColyseusProvider] Received tableCreatedSuccess:",
            payload
          );
          if (disposed) {
            console.warn(
              "[ColyseusProvider] Disposed, ignoring tableCreatedSuccess"
            );
            return;
          }
          // Emit both events: tableCreatedSuccess for creator callback, tableCreated for general sync
          console.log("[ColyseusProvider] Emitting tableCreatedSuccess event");
          globalEventBus.emit("tableCreatedSuccess", { table: payload.table });
          console.log("[ColyseusProvider] Emitting tableCreated event");
          globalEventBus.emit("tableCreated", { table: payload.table });
        });

        room.onMessage("joinTableSuccess", (payload) => {
          if (disposed) return;
          // Update the table in local state with new player list
          if (payload.tableId) {
            setLobbyState((prev) => ({
              ...prev,
              tables: prev.tables.map((t) =>
                t.id === payload.tableId
                  ? {
                      ...t,
                      players: payload.players || t.players,
                      spectators: payload.spectators || t.spectators,
                    }
                  : t
              ),
            }));
          }
        });

        room.onMessage("lobbySnapshot", (payload) => {
          if (disposed) return;
          try {
            setLobbyState((prev) => ({
              ...prev,
              onlinePlayers: Array.isArray(payload?.players)
                ? payload.players.map((p) => ({
                    id: p.id,
                    username: p.username,
                    characterId: p.characterId,
                    isReady: p.isReady,
                    isHost: p.isHost,
                    connectedAt: p.connectedAt,
                  }))
                : prev.onlinePlayers,
              tables: Array.isArray(payload?.tables)
                ? payload.tables.map((t) => ({
                    id: t.id,
                    name: t.name,
                    mode: t.mode,
                    maxPlayers: t.maxPlayers,
                    currentPlayers: t.currentPlayers,
                    status: t.status,
                    hostId: t.hostId,
                    isPrivate: t.isPrivate,
                    createdAt: t.createdAt,
                    players: t.players || [],
                    spectators: t.spectators || [],
                    selectedCharacters: t.selectedCharacters || {},
                  }))
                : prev.tables,
            }));
          } catch (e) {
            console.error(
              "[ColyseusProvider] Error processing lobbySnapshot:",
              e
            );
          }
        });

        room.onMessage("tableCreated", (payload) => {
          if (disposed) return;

          // Add table to lobbyState
          if (payload.table) {
            setLobbyState((prev) => {
              // Check if table already exists
              const exists = prev.tables.some((t) => t.id === payload.table.id);
              if (exists) {
                return prev;
              }
              return {
                ...prev,
                tables: [...prev.tables, payload.table],
              };
            });
          }

          globalEventBus.emit("tableCreated", { table: payload.table });
        });

        room.onMessage("tableUpdated", (payload) => {
          if (disposed) return;

          // Update table in local state immediately
          if (payload.table?.id) {
            setLobbyState((prev) => ({
              ...prev,
              tables: prev.tables.map((t) =>
                t.id === payload.table.id ? { ...t, ...payload.table } : t
              ),
            }));
          }

          globalEventBus.emit("tableUpdated", { table: payload.table });
        });

        room.onMessage("tableRemoved", (payload) => {
          if (disposed) return;
          globalEventBus.emit("tableDeleted", { tableId: payload.tableId });
        });

        room.onMessage("characterSelected", (payload) => {
          if (disposed) return;
          console.log("[ColyseusProvider] Character selected:", payload);

          // Update table in lobbyState
          if (payload.tableId && payload.username) {
            setLobbyState((prev) => ({
              ...prev,
              tables: prev.tables.map((t) =>
                t.id === payload.tableId
                  ? {
                      ...t,
                      selectedCharacters: {
                        ...t.selectedCharacters,
                        [payload.username]: payload.allCharacters,
                      },
                    }
                  : t
              ),
            }));
          }

          globalEventBus.emit("characterSelected", payload);
        });

        room.onMessage("characterDeselected", (payload) => {
          if (disposed) return;
          console.log("[ColyseusProvider] Character deselected:", payload);

          // Update table in lobbyState
          if (payload.tableId && payload.username) {
            setLobbyState((prev) => ({
              ...prev,
              tables: prev.tables.map((t) =>
                t.id === payload.tableId
                  ? {
                      ...t,
                      selectedCharacters: {
                        ...t.selectedCharacters,
                        [payload.username]: payload.allCharacters,
                      },
                    }
                  : t
              ),
            }));
          }

          globalEventBus.emit("characterDeselected", payload);
        });

        setConnected(true);
      } catch (error) {
        console.error("[ColyseusProvider] Lobby connection failed:", error);
        setConnected(false);
        setLastError(error?.message || String(error));
      }
    })();

    return () => {
      disposed = true;
      (async () => {
        try {
          if (currentRoom) {
            await currentRoom.leave(true);
          } else {
            await clientRef.current?.leaveLobby();
          }
        } catch (e) {
          console.error("[ColyseusProvider] Error leaving lobby:", e);
        }
      })();

      lobbyRoomRef.current = null;
      setConnected(false);
      setLobbyState({ tables: [], messages: [], onlinePlayers: [] });
    };
  }, [username]);

  // Broadcast tables to the EventBus when they change
  useEffect(() => {
    try {
      if (Array.isArray(lobbyState?.tables)) {
        globalEventBus.emit("tables", lobbyState.tables);
      }
    } catch (e) {
      console.error("[ColyseusProvider] EventBus emit tables error:", e);
    }
  }, [lobbyState?.tables]);

  // Keep username in sync with localStorage and app events
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === "gg_user") {
        const next = getPersistedUsername();
        if (next !== username) setUsername(next);
      }
    };
    const handleFocus = () => {
      const next = getPersistedUsername();
      if (next !== username) setUsername(next);
    };
    const handleAuthChanged = (e) => {
      const next = e?.detail?.user?.username || null;
      if (next !== username) setUsername(next);
    };
    window.addEventListener("storage", handleStorage);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("gg:auth-changed", handleAuthChanged);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("gg:auth-changed", handleAuthChanged);
    };
  }, [username]);

  const sendLobbyChat = (text) => {
    if (!lobbyRoomRef.current || !text?.trim()) return;
    lobbyRoomRef.current.send("chat:send", { text });
  };

  const createTable = (tableData) => {
    if (!lobbyRoomRef.current) {
      console.error("[ColyseusProvider] Cannot create table - not connected");
      return false;
    }
    console.log("[ColyseusProvider] Sending createTable message:", tableData);
    lobbyRoomRef.current.send("createTable", tableData);
    console.log("[ColyseusProvider] createTable message sent successfully");
    return true;
  };

  const joinTable = (tableId) => {
    if (!lobbyRoomRef.current) {
      console.error("[ColyseusProvider] Cannot join table - not connected");
      return false;
    }
    console.log("[ColyseusProvider] Sending joinTable message:", { tableId });
    lobbyRoomRef.current.send("joinTable", { tableId });
    return true;
  };

  const leaveTable = (tableId) => {
    if (!lobbyRoomRef.current) {
      console.error("[ColyseusProvider] Cannot leave table - not connected");
      return false;
    }
    lobbyRoomRef.current.send("leaveTable", { tableId });
    return true;
  };

  const selectCharacter = (tableId, characterData) => {
    if (!lobbyRoomRef.current) {
      console.error(
        "[ColyseusProvider] Cannot select character - not connected"
      );
      return false;
    }
    console.log("[ColyseusProvider] Selecting character:", {
      tableId,
      characterData,
    });
    lobbyRoomRef.current.send("selectCharacter", { tableId, characterData });
    return true;
  };

  const deselectCharacter = (tableId, characterId) => {
    if (!lobbyRoomRef.current) {
      console.error(
        "[ColyseusProvider] Cannot deselect character - not connected"
      );
      return false;
    }
    console.log("[ColyseusProvider] Deselecting character:", {
      tableId,
      characterId,
    });
    lobbyRoomRef.current.send("deselectCharacter", { tableId, characterId });
    return true;
  };

  return (
    <ColyseusContext.Provider
      value={{
        connected,
        lobbyState,
        sendLobbyChat,
        createTable,
        joinTable,
        leaveTable,
        selectCharacter,
        deselectCharacter,
        rawLobbyRoom: lobbyRoomRef.current,
        connectionAttempted,
        lastError,
      }}
    >
      {children}
    </ColyseusContext.Provider>
  );
}
